import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  TooltipProps,
} from 'recharts';
import { Droplet, Loader2 } from 'lucide-react';

/**
 * Interface for milk production data point
 */
export interface MilkProductionData {
  date: string;
  liters: number;
  quality?: 'excellent' | 'good' | 'average' | 'poor';
}

interface MilkProductionChartProps {
  data: MilkProductionData[];
  loading?: boolean;
  error?: string | null;
  height?: number;
}

/**
 * Custom tooltip component for milk production display
 */
const CustomTooltip: React.FC<TooltipProps<number, string>> = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const qualityColors = {
      excellent: 'text-green-600',
      good: 'text-blue-600',
      average: 'text-yellow-600',
      poor: 'text-red-600',
    };

    const quality = payload[0].payload.quality;
    const qualityColor = quality ? qualityColors[quality as keyof typeof qualityColors] : 'text-gray-600';

    return (
      <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
        <p className="font-semibold text-gray-700">{label}</p>
        <p className="text-blue-600">
          Production: <span className="font-bold">{payload[0].value} L</span>
        </p>
        {quality && (
          <p className={`text-sm ${qualityColor} capitalize font-medium`}>
            Quality: {quality}
          </p>
        )}
      </div>
    );
  }
  return null;
};

/**
 * MilkProductionChart - Line chart showing daily milk production trend
 *
 * Features:
 * - Displays daily production in liters
 * - Blue color scheme for dairy/liquid theme
 * - Interactive tooltips with quality information
 * - Smooth curve for better trend visualization
 * - Responsive design for all devices
 * - Loading and error states
 *
 * @param data - Array of daily milk production data points
 * @param loading - Loading state indicator
 * @param error - Error message if data fetch failed
 * @param height - Chart height in pixels (default: 350)
 */
const MilkProductionChart: React.FC<MilkProductionChartProps> = ({
  data,
  loading = false,
  error = null,
  height = 350,
}) => {
  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ height }}>
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Loading production data...</span>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center" style={{ height }}>
        <div className="text-center">
          <p className="text-red-600 font-semibold">Failed to load chart</p>
          <p className="text-sm text-gray-500 mt-1">{error}</p>
        </div>
      </div>
    );
  }

  // Empty state
  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center" style={{ height }}>
        <div className="text-center text-gray-500">
          <Droplet className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p>No production data available</p>
        </div>
      </div>
    );
  }

  // Calculate statistics
  const totalProduction = data.reduce((sum, item) => sum + item.liters, 0);
  const averageProduction = (totalProduction / data.length).toFixed(1);
  const maxProduction = Math.max(...data.map(item => item.liters));

  return (
    <div className="w-full">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-800 flex items-center">
          <Droplet className="w-5 h-5 mr-2 text-blue-600" />
          Daily Milk Production
        </h3>
        <p className="text-sm text-gray-500 mt-1">
          Tracking production trends and quality metrics
        </p>
      </div>

      {/* Statistics Summary */}
      <div className="grid grid-cols-3 gap-4 mb-4">
        <div className="bg-blue-50 p-3 rounded-lg">
          <p className="text-xs text-gray-600">Total</p>
          <p className="text-lg font-bold text-blue-600">{totalProduction.toLocaleString()} L</p>
        </div>
        <div className="bg-blue-50 p-3 rounded-lg">
          <p className="text-xs text-gray-600">Average</p>
          <p className="text-lg font-bold text-blue-600">{averageProduction} L</p>
        </div>
        <div className="bg-blue-50 p-3 rounded-lg">
          <p className="text-xs text-gray-600">Peak</p>
          <p className="text-lg font-bold text-blue-600">{maxProduction} L</p>
        </div>
      </div>

      <ResponsiveContainer width="100%" height={height}>
        <LineChart
          data={data}
          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
          <XAxis
            dataKey="date"
            stroke="#666"
            tick={{ fontSize: 12 }}
            tickMargin={10}
          />
          <YAxis
            stroke="#666"
            tick={{ fontSize: 12 }}
            label={{ value: 'Production (Liters)', angle: -90, position: 'insideLeft' }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend
            wrapperStyle={{ paddingTop: '20px' }}
            iconType="line"
          />
          <Line
            type="monotone"
            dataKey="liters"
            name="Milk Production (L)"
            stroke="#3b82f6"
            strokeWidth={3}
            dot={{ fill: '#3b82f6', r: 4 }}
            activeDot={{ r: 6, fill: '#2563eb' }}
            animationDuration={1000}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default MilkProductionChart;
